import { VacationBonusSortField, PaginatedVacationBonusQueryVariables } from "@/graphql/__generated__/graphql";
import { useTranslation } from "react-i18next";
import SortDropdownFilter from "../../Shared/SortDropdownFilter";

interface Props {
  displayAmount: number;
  handleSubmitClick: (_variables: PaginatedVacationBonusQueryVariables) => void;
}

/**
 * VacationBonusSortingDropdown component
 * Provides a dropdown menu for sorting vacation bonus data by different fields and orders
 *
 * @param {Object} props - Component props
 * @param {number} props.displayAmount - Number of items to display per page
 * @param {Function} props.handleSubmitClick - Function to handle submitting the updated query variables
 * @returns {JSX.Element} Sorting dropdown component
 */
const VacationBonusSortingDropdown = (props: Props) => {
  const { t } = useTranslation(["translation", "common", "components"]);

  /**
   * Gets user-friendly names for sort fields
   * @param {VacationBonusSortField} field - The sort field to get a display name for
   * @returns {string} User-friendly name for the sort field
   */
  const getSortFieldName = (field: VacationBonusSortField): string => {
    switch (field) {
    case VacationBonusSortField.BonusRelative:
      return t("components:filters.vacationbonus.sortfields.bonusrelative");
    case VacationBonusSortField.BonusAbsolute:
      return t("components:filters.vacationbonus.sortfields.bonusabsolute");
    case VacationBonusSortField.ActiveFrom:
      return t("components:filters.vacationbonus.sortfields.activefrom");
    case VacationBonusSortField.Province:
      return t("components:filters.vacationbonus.sortfields.province");
    default:
      return field;
    }
  };

  const vacationBonusSortConfig = {
    paginationStateKey: "paginatedVacationBonus" as keyof PaginationState,
    sortFields: VacationBonusSortField,
    getSortFieldName,
    defaultSortField: VacationBonusSortField.ActiveFrom,
    buildQueryVariables: (displayAmount: number, filters: any, sort: { field: VacationBonusSortField; order: any } | null): PaginatedVacationBonusQueryVariables => ({
      first: displayAmount,
      filters: {
        activeFrom: filters.activeFrom,
        bonusRelative: filters.bonusRelative,
        bonusAbsolute: filters.bonusAbsolute,
        provinces: filters.provinces,
      },
      sort: sort
    }),
    extractFilters: (paginationState: any) => ({
      activeFrom: paginationState.filters?.activeFrom,
      bonusRelative: paginationState.filters?.bonusRelative,
      bonusAbsolute: paginationState.filters?.bonusAbsolute,
      provinces: paginationState.filters?.provinces,
    })
  };

  return (
    <SortDropdownFilter
      displayAmount={props.displayAmount}
      handleSubmitClick={props.handleSubmitClick}
      config={vacationBonusSortConfig}
    />
  );
};

export default VacationBonusSortingDropdown;
